import { useEffect, useMemo, useRef, useState } from 'react';
import './MultiSelectDropdown.scss';
import SearchInput from '../SearchInput/SearchInput';

type Option = {
  value: string;
  label: string;
};

type Props = {
  label: string;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
};

export function MultiSelectDropdown({
  label,
  options,
  value,
  onChange,
  placeholder = 'All',
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const selectedText = useMemo(() => {
    if (!value.length) return placeholder;

    if (value.length === 1) {
      return options.find((o) => o.value === value[0])?.label || '1 selected';
    }

    return `${value.length} selected`;
  }, [value, options, placeholder]);

  const visibleOptions = useMemo(() => {
    const term = search.trim().toLowerCase();

    return options
      .filter((opt) => !term || opt.label.toLowerCase().includes(term))
      .sort((a, b) => {
        const aSelected = selectedSet.has(a.value);
        const bSelected = selectedSet.has(b.value);

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        return a.label.localeCompare(b.label);
      });
  }, [options, search, selectedSet]);

  const toggleValue = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div className="form-control multi-select" ref={wrapperRef}>
      <label>{label}</label>

      <button type="button" className="multi-select__button" onClick={() => setOpen((v) => !v)}>
        <span>{selectedText}</span>
        <span className="multi-select__chevron">▾</span>
      </button>

      {open && (
        <div className="multi-select__menu">
          <div className="multi-select__search-wrap">
            <SearchInput
              className="multi-select__search"
              value={search}
              onValueChange={setSearch}
              placeholder="Search..."
              autoFocus
            />
          </div>

          <button type="button" className="multi-select__clear" onClick={() => onChange([])}>
            Clear selection
          </button>

          <div className="multi-select__options">
            {visibleOptions.length === 0 ? (
              <div className="multi-select__empty">No matches</div>
            ) : (
              visibleOptions.map((opt) => (
                <label key={opt.value} className="multi-select__option">
                  <input
                    type="checkbox"
                    checked={value.includes(opt.value)}
                    onChange={() => toggleValue(opt.value)}
                  />
                  <span className="multi-select__option-text">{opt.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
